package or.garden.manager.features.catalog.components

import androidx.compose.runtime.Composable
import or.garden.manager.domain.model.CropData
import or.garden.manager.domain.model.NutrientStage
import or.garden.manager.domain.model.TransplantWindow
import or.garden.manager.ui.utils.getCropImageSource
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.dom.*

@Composable
fun CropDetailsView(
    crop: CropData,
    onBack: () -> Unit,
    onToggleFavorite: () -> Unit,
    isTogglingFavorite: Boolean
) {
    Div({ classes("max-w-4xl", "mx-auto") }) {
        // Back Button & Title
        Div({ classes("flex", "items-center", "gap-4", "mb-8") }) {
            Button({
                classes(
                    "w-10", "h-10", "rounded-xl", "bg-white", "border", "border-gray-200", "flex", "items-center", "justify-center", "text-gray-600", "hover:bg-emerald-50", "hover:text-emerald-600", "hover:border-emerald-100", "transition-all", "shadow-sm"
                )
                onClick { onBack() }
            }) {
                I({ classes("fas", "fa-arrow-left") })
            }
            H1({ classes("text-3xl", "font-bold", "text-emerald-900") }) { Text(crop.name) }

            Div({ classes("flex-1") })

            Button({
                classes(
                    "px-4", "py-2", "rounded-xl", "flex", "items-center", "gap-2", "font-bold", "transition-all", if (isTogglingFavorite) "opacity-50" else "opacity-100"
                )
                if (crop.isFavourite) {
                    classes("bg-red-50", "text-red-600")
                } else {
                    classes("bg-gray-100", "text-gray-600", "hover:bg-gray-200")
                }
                if (isTogglingFavorite) disabled()
                onClick { onToggleFavorite() }
            }) {
                if (isTogglingFavorite) {
                    I({ classes("fas", "fa-circle-notch", "fa-spin") })
                    Text("Sačekajte...")
                } else {
                    I({ classes(if (crop.isFavourite) "fas" else "far", "fa-heart") })
                    Text(if (crop.isFavourite) "Ukloni iz omiljenih" else "Dodaj u omiljene")
                }
            }
        }

        Div({ classes("grid", "grid-cols-1", "lg:grid-cols-3", "gap-8") }) {
            // Left Column - Image & Quick Stats
            Div({ classes("lg:col-span-1", "space-y-6") }) {
                Div({
                    classes(
                        "aspect-square", "bg-white", "rounded-3xl", "border", "border-gray-100", "flex", "items-center", "justify-center", "shadow-sm", "p-12"
                    )
                }) {
                    val imageSource = getCropImageSource(crop.icon)
                    if (imageSource != null) {
                        Img(src = imageSource, attrs = {
                            classes("w-full", "h-full", "object-contain")
                        })
                    } else {
                        I({ classes("fas", "fa-seedling", "text-emerald-500", "text-7xl") })
                    }
                }

                // Metadata Cards
                Div({
                    classes(
                        "bg-white", "rounded-2xl", "p-6", "border", "border-gray-100", "shadow-sm", "space-y-4"
                    )
                }) {
                    DetailItem("Latinski naziv", crop.latinName ?: "Nije unijeto", "fa-italic")
                    DetailItem(
                        "Visina biljke", if (crop.height != null) "${crop.height} cm" else "Nije unijeto", "fa-arrows-alt-v"
                    )
                    DetailItem("Razmak", crop.spacing(), "fa-expand-arrows-alt")
                    DetailItem(
                        "Potrošnja resursa", when (crop.resourceConsumption?.lowercase()) {
                            "high" -> "Visoka"; "medium" -> "Srednja"; "low" -> "Niska"; else -> "Srednja"
                        }, "fa-chart-pie"
                    )
                }
            }

            // Right Column - Description & More Info
            Div({ classes("lg:col-span-2", "space-y-6") }) {
                // Description
                Div({
                    classes("bg-white", "rounded-3xl", "p-8", "border", "border-gray-100", "shadow-sm")
                }) {
                    H3({ classes("text-xl", "font-bold", "text-gray-900", "mb-4") }) { Text("Opis") }
                    P({ classes("text-gray-600", "leading-relaxed") }) {
                        Text(crop.description ?: "Nema opisa za ovaj usev.")
                    }
                }

                // Growing Requirements
                Div({ classes("grid", "grid-cols-1", "md:grid-cols-2", "gap-6") }) {
                    RequirementCard(
                        "Navodnjavanje", when (crop.waterRequirements?.lowercase()) {
                            "high" -> "Često"; "medium" -> "Umjereno"; "low" -> "Rijetko"; else -> "Srednje"
                        }, "fa-tint", "text-blue-600", "bg-blue-50"
                    )
                    RequirementCard(
                        "Temperatura", if (crop.optimalTemperatureMin != null) "${crop.optimalTemperatureMin}°C - ${crop.optimalTemperatureMax}°C" else "Umjerena", "fa-thermometer-half", "text-orange-600", "bg-orange-50"
                    )
                    RequirementCard(
                        "Otpornost na mraz", when (crop.frostSensitivity?.lowercase()) {
                            "sensitive" -> "Osjetljivo"; "hardy" -> "Otporno"; else -> "Nepoznato"
                        }, "fa-snowflake", "text-cyan-600", "bg-cyan-50"
                    )
                    RequirementCard(
                        "Min. Temperatura", if (crop.minTemperature != null) "${crop.minTemperature}°C" else "Nepoznato", "fa-temperature-low", "text-indigo-600", "bg-indigo-50"
                    )
                }

                // Transplant Schedule
                crop.transplantSchedule?.let { schedule ->
                    Div({
                        classes("bg-white", "rounded-3xl", "p-8", "border", "border-gray-100", "shadow-sm")
                    }) {
                        H3({ classes("text-xl", "font-bold", "text-gray-900", "mb-6") }) { Text("Plan sadnje") }
                        Div({ classes("grid", "grid-cols-1", "md:grid-cols-3", "gap-4") }) {
                            ScheduleItem("Kontinentalna", schedule.continental)
                            ScheduleItem("Mediteranska", schedule.mediterranean)
                            ScheduleItem("Planinska", schedule.mountain)
                        }
                    }
                }

                // Nutrients
                crop.nutrients?.let { nutrients ->
                    if (nutrients.isNotEmpty()) {
                        Div({
                            classes("bg-white", "rounded-3xl", "p-8", "border", "border-gray-100", "shadow-sm")
                        }) {
                            H3({ classes("text-xl", "font-bold", "text-gray-900", "mb-6") }) { Text("Ishrana po fazama") }
                            Div({ classes("space-y-4") }) {
                                nutrients.forEach { stage ->
                                    NutrientStageRow(stage)
                                }
                            }
                        }
                    }
                }

                // Companions
                if (crop.goodCompanions.isNotEmpty() || crop.badCompanions.isNotEmpty()) {
                    Div({
                        classes("bg-white", "rounded-3xl", "p-8", "border", "border-gray-100", "shadow-sm")
                    }) {
                        H3({ classes("text-xl", "font-bold", "text-gray-900", "mb-6") }) { Text("Komšije u bašti") }

                        if (crop.goodCompanions.isNotEmpty()) {
                            Div({ classes("mb-6") }) {
                                Span({
                                    classes("text-sm", "font-bold", "text-emerald-600", "uppercase", "tracking-wider", "block", "mb-3")
                                }) {
                                    I({ classes("fas", "fa-check-circle", "mr-2") })
                                    Text("Dobri susjedi")
                                }
                                Div({ classes("flex", "flex-wrap", "gap-2") }) {
                                    crop.goodCompanions.forEach { companion ->
                                        CompanionTag(companion.name, true)
                                    }
                                }
                            }
                        }

                        if (crop.badCompanions.isNotEmpty()) {
                            Div {
                                Span({
                                    classes("text-sm", "font-bold", "text-red-500", "uppercase", "tracking-wider", "block", "mb-3")
                                }) {
                                    I({ classes("fas", "fa-times-circle", "mr-2") })
                                    Text("Loši susjedi")
                                }
                                Div({ classes("flex", "flex-wrap", "gap-2") }) {
                                    crop.badCompanions.forEach { companion ->
                                        CompanionTag(companion.name, false)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun DetailItem(label: String, value: String, icon: String) {
    Div({ classes("flex", "items-center", "gap-3") }) {
        Div({
            classes("w-8", "h-8", "rounded-lg", "bg-emerald-50", "flex", "items-center", "justify-center", "text-emerald-600", "text-sm")
        }) {
            I({ classes("fas", icon) })
        }
        Div {
            Span({ classes("text-xs", "text-gray-400", "block") }) { Text(label) }
            Span({ classes("text-sm", "font-bold", "text-gray-700") }) { Text(value) }
        }
    }
}

@Composable
fun RequirementCard(title: String, value: String, icon: String, textColor: String, bgColor: String) {
    Div({ classes("p-6", "rounded-2xl", bgColor, "flex", "items-center", "gap-4") }) {
        Div({
            classes("w-12", "h-12", "rounded-xl", "bg-white/80", "flex", "items-center", "justify-center", textColor, "text-xl")
        }) {
            I({ classes("fas", icon) })
        }
        Div {
            Span({
                classes("text-xs", "font-bold", textColor, "uppercase", "tracking-wider", "block")
            }) { Text(title) }
            Span({ classes("text-lg", "font-black", "text-gray-900") }) { Text(value) }
        }
    }
}

@Composable
fun ScheduleItem(region: String, window: TransplantWindow?) {
    Div({ classes("p-4", "rounded-2xl", "border", "border-gray-50", "bg-gray-50/50") }) {
        Span({
            classes("text-xs", "font-bold", "text-gray-400", "uppercase", "block", "mb-2")
        }) { Text(region) }
        if (window != null) {
            Div({ classes("flex", "items-center", "gap-2", "text-emerald-700", "font-bold") }) {
                I({ classes("far", "fa-calendar-check") })
                Text("${window.startMonthName} - ${window.endMonthName}")
            }
            if (window.notes != null) {
                P({ classes("text-xs", "text-gray-500", "mt-2", "italic") }) { Text(window.notes!!) }
            }
        } else {
            Span({ classes("text-sm", "text-gray-400") }) { Text("Nema podataka") }
        }
    }
}

@Composable
fun NutrientStageRow(stage: NutrientStage) {
    Div({
        classes("flex", "flex-col", "md:flex-row", "md:items-center", "justify-between", "p-4", "rounded-2xl", "border", "border-gray-50", "bg-gray-50/50", "gap-4")
    }) {
        Div {
            val phaseName = when (stage.phaseName) {
                "transplanting" -> "Presađivanje"
                "flowering" -> "Cvjetanje"
                "fruiting" -> "Plodonošenje"
                else -> stage.phaseName ?: "Faza"
            }
            Span({ classes("font-bold", "text-gray-800") }) { Text(phaseName) }
            Span({ classes("text-xs", "text-gray-500", "block") }) {
                Text(if (stage.weekStart != null) "Nedjelja: ${stage.weekStart}${if (stage.weekEnd != null) "-${stage.weekEnd}" else ""}" else "Vrijeme nije definisano")
            }
        }
        Div({ classes("flex", "gap-3") }) {
            NutrientBadge("N", stage.nitrogen, "bg-blue-100", "text-blue-700")
            NutrientBadge("P", stage.phosphorus, "bg-purple-100", "text-purple-700")
            NutrientBadge("K", stage.potassium, "bg-orange-100", "text-orange-700")
        }
    }
}

@Composable
fun NutrientBadge(label: String, value: String?, bgColor: String, textColor: String) {
    Div({ classes("flex", "items-center", "gap-1.5", "px-3", "py-1.5", "rounded-xl", bgColor) }) {
        Span({ classes("text-[10px]", "font-black", textColor) }) { Text(label) }
        val translatedValue = when (value?.lowercase()) {
            "low" -> "Nisko"
            "medium" -> "Srednje"
            "high" -> "Visoko"
            "very high" -> "Vrlo visoko"
            else -> value ?: "-"
        }
        Span({ classes("text-xs", "font-bold", "text-gray-700") }) { Text(translatedValue) }
    }
}

@Composable
fun CompanionTag(name: String, isGood: Boolean) {
    Div({
        classes(
            "px-4", "py-2", "rounded-xl", "text-sm", "font-bold", if (isGood) "bg-emerald-50" else "bg-red-50", if (isGood) "text-emerald-700" else "text-red-600", "border", if (isGood) "border-emerald-100" else "border-red-100"
        )
    }) {
        Text(name)
    }
}
